import React, { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { api } from "@/lib/api";

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.includes("@") || !form.message.trim()) {
            toast.error("Please fill in your name, email, and a short message.");
            return;
        }
        setBusy(true);
        try {
            await api.post("/contact", form);
            setSent(true);
            setForm({ name: "", email: "", subject: "", message: "" });
        } catch {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="pt-32 md:pt-40 pb-16" data-testid="contact-page">
            <section className="mx-auto max-w-[1280px] px-6 md:px-10">
                <div className="grid lg:grid-cols-12 gap-8 items-center">
                    <div className="lg:col-span-7">
                        <p className="overline">Let&apos;s Create Together</p>
                        <h1 className="mt-4 text-3xl md:text-5xl font-bold text-[rgb(var(--teal))] tracking-tight leading-[1.1]">
                            I&apos;m happiest helping people think through
                            <br className="hidden md:block" />{" "}
                            <span className="italic text-[rgb(var(--orange))]">ambitious ideas.</span>
                        </h1>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="frame aspect-[5/4] max-w-[440px] ml-auto">
                            <img
                                src="https://customer-assets.emergentagent.com/job_couch-conversations/artifacts/znxcwvyt_PXL_20241026_180331127.webp"
                                alt="Leah under a stone archway, arms up, ocean behind her — mid-thought, mid-adventure."
                            />
                        </div>
                    </div>
                </div>
            </section>

            <div className="mx-auto max-w-[1280px] px-6 md:px-10 mt-10">
                <div className="rule" />
            </div>

            <section className="mx-auto max-w-[1280px] px-6 md:px-10 py-12 md:py-16 grid md:grid-cols-12 gap-10">
                <div className="md:col-span-5">
                    <div className="space-y-4 text-[17px] leading-relaxed text-[rgb(var(--ink-2))]">
                        <p>Sometimes that&apos;s through writing.</p>
                        <p>Sometimes it&apos;s designing education systems.</p>
                        <p>Sometimes it&apos;s speaking, facilitating, or helping organizations imagine what&apos;s possible.</p>
                        <p className="text-[rgb(var(--teal))] font-semibold">
                            If you&apos;re working on something interesting, I&apos;d love to hear about it.
                        </p>
                    </div>

                    <div className="mt-10 border-t hairline pt-6">
                        <p className="overline">Also, professionally</p>
                        <a
                            href="https://elenemstrategies.com"
                            target="_blank"
                            rel="noreferrer"
                            className="mt-3 inline-flex items-baseline gap-2 text-xl md:text-2xl font-bold text-[rgb(var(--teal))] hover:text-[rgb(var(--orange))] transition-colors"
                            data-testid="contact-elenem-link"
                        >
                            → Learn more about Elenem Strategies
                        </a>
                    </div>
                </div>

                <div className="md:col-span-7">
                    {sent ? (
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="border hairline p-10 text-center"
                        >
                            <p className="text-2xl italic text-[rgb(var(--orange))]">Your note landed.</p>
                            <p className="mt-3 text-[15px] text-[rgb(var(--ink-2))]">
                                I read every one. I&apos;ll write back soon.
                            </p>
                            <button
                                onClick={() => setSent(false)}
                                className="btn-ghost mt-6"
                                data-testid="contact-send-another"
                            >
                                Send another →
                            </button>
                        </motion.div>
                    ) : (
                        <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <label className="overline">Your name</label>
                                    <input
                                        type="text"
                                        required
                                        value={form.name}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        className="input-underline mt-2"
                                        data-testid="contact-name"
                                    />
                                </div>
                                <div>
                                    <label className="overline">Email</label>
                                    <input
                                        type="email"
                                        required
                                        value={form.email}
                                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                                        className="input-underline mt-2"
                                        data-testid="contact-email"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="overline">Subject</label>
                                <input
                                    type="text"
                                    value={form.subject}
                                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                                    className="input-underline mt-2"
                                    data-testid="contact-subject"
                                />
                            </div>
                            <div>
                                <label className="overline">The idea</label>
                                <textarea
                                    required
                                    rows={6}
                                    value={form.message}
                                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                                    className="input-underline mt-2 resize-none"
                                    data-testid="contact-message"
                                />
                            </div>
                            <div className="pt-2">
                                <button
                                    type="submit"
                                    disabled={busy}
                                    className="btn-primary disabled:opacity-50"
                                    data-testid="contact-submit"
                                >
                                    {busy ? "Sending…" : "Send the note →"}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </section>
        </div>
    );
}
